import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { useRequireAdmin } from "@/lib/useRequireAdmin";

const Row = ({ name, ok, hint }) => (
  <div
    className={`flex items-center justify-between rounded-lg px-3 py-2 border ${
      ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"
    }`}
    data-testid={`env-row-${name}`}
  >
    <div className="flex items-center gap-2 min-w-0">
      <span>{ok ? "✅" : "❌"}</span>
      <code className="text-sm text-white truncate">{name}</code>
      {hint && <span className="text-xs text-gray-400">({hint})</span>}
    </div>
    <span className={`text-xs font-bold ${ok ? "text-emerald-300" : "text-red-300"}`}>
      {ok ? "SET" : "MISSING"}
    </span>
  </div>
);

const AdminServiceStatusPage = () => {
  const { authChecked } = useRequireAdmin();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!authChecked) return;
    api.get("/admin/service-status")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [authChecked]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0a0a0e] p-6">
        <p className="text-red-300" data-testid="service-status-error">{err}</p>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#0a0a0e] p-6 text-gray-300">Loading…</div>;

  const lk = data.recording?.livekit || {};
  const cd = data.recording?.cloudinary || {};
  const allGood = data.egress_configured;
  const missing = [];
  for (const [k, v] of Object.entries(lk)) if (!v.set) missing.push(k);
  for (const [k, v] of Object.entries(cd)) if (!v.set) missing.push(k);

  return (
    <div className="min-h-screen bg-[#0a0a0e] py-8 px-4 sm:px-6" data-testid="admin-service-status-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/admin"><Button variant="ghost" className="text-purple-300 mb-4">← Admin</Button></Link>
        <h1 className="text-3xl font-bold text-white mb-2">🛠 Service Status</h1>
        <p className="text-pink-200/70 mb-6">
          Production-environment health checks. Values are never displayed —
          only presence.
        </p>

        <Card className="p-5 bg-[#101019] border-white/10 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-white">🎥 LiveKit Recording (Egress)</h2>
            <span
              className={`text-xs font-black uppercase px-2 py-1 rounded ${
                allGood ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"
              }`}
              data-testid="recording-overall-status"
            >
              {allGood ? "READY" : "NOT READY"}
            </span>
          </div>

          <p className="text-xs uppercase tracking-wider text-amber-200 mt-3 mb-2">LiveKit</p>
          <div className="space-y-1.5">
            {Object.entries(lk).map(([name, v]) => (
              <Row key={name} name={name} ok={v.set} hint={v.hint} />
            ))}
          </div>

          <p className="text-xs uppercase tracking-wider text-amber-200 mt-4 mb-2">Cloudinary (recording storage)</p>
          <div className="space-y-1.5">
            {Object.entries(cd).map(([name, v]) => (
              <Row key={name} name={name} ok={v.set} hint={v.hint} />
            ))}
          </div>

          {!allGood && (
            <div
              className="mt-5 p-4 rounded-lg border border-amber-400/40 bg-amber-500/10"
              data-testid="support-ready-block"
            >
              <p className="text-sm font-bold text-amber-100 mb-2">📩 Email Emergent Support</p>
              <p className="text-xs text-amber-100/90 mb-2">
                Copy the message below to ship to <a className="underline" href="mailto:support@emergent.sh">support@emergent.sh</a>:
              </p>
              <pre
                className="text-[11px] text-emerald-100 bg-black/40 rounded p-3 whitespace-pre-wrap overflow-x-auto"
                data-testid="support-ready-message"
              >{`Hi Emergent Support,

Please add the following environment variables to the production pod for rgmstudio.org:

${missing.map((m) => "  - " + m).join("\n")}

These power LiveKit Egress recording → Cloudinary S3 storage. Without them the Start Recording button returns a 503 "Recording is not configured on this server" error.

Thanks.`}</pre>
            </div>
          )}

          {allGood && (
            <p className="mt-5 text-sm text-emerald-200" data-testid="recording-ready-confirmation">
              ✅ All required env vars are set. The Start Recording button will work end-to-end.
            </p>
          )}
        </Card>

        <p className="text-xs text-gray-500 text-center">
          Server timestamp: <code>{data.server_ts}</code>
        </p>
      </div>
    </div>
  );
};

export default AdminServiceStatusPage;
